import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Building2, User, Phone, Briefcase, ArrowRight, AlertCircle,
  CheckCircle2, Loader2, Shield,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

// Garde-fous front-end (le backend a la VRAIE vérité). On les duplique côté
// client pour donner un feedback instantané sans round-trip.
const GARBAGE_LOCAL = new Set([
  'test', 'tests', 'testing', 'tester',
  'demo', 'demos', 'trial',
  'admin', 'administrator', 'root', 'superuser',
  'user', 'users', 'username',
  'asdf', 'qwerty', 'azerty', 'abc', 'abcd', 'abcde',
  'noreply', 'no-reply', 'donotreply', 'do-not-reply', 'nobody',
  'example', 'examples', 'sample', 'samples',
  'foo', 'bar', 'baz', 'lorem', 'ipsum',
  'hello', 'hi', 'salut', 'bonjour', 'info', 'contact',
  'fake', 'anonymous', 'anon', 'void', 'null', 'none',
]);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ' \-]+$/;

function validateEmailShape(email) {
  const e = (email || '').trim().toLowerCase();
  if (!e) return "Email requis";
  if (!EMAIL_RE.test(e)) return "Format d'email invalide";
  const [local, domain] = e.split('@');
  if (local.length < 2) return "Adresse email invalide";
  if (GARBAGE_LOCAL.has(local)) return "Merci d'utiliser une adresse email réelle";
  if (new Set(local).size <= 2) return "Merci d'utiliser une adresse email réelle";
  if (!domain.includes('.')) return "Domaine email invalide";
  return null;
}

function validateNameShape(name) {
  const n = (name || '').trim();
  if (n.length < 3) return "Nom trop court";
  if (n.length > 100) return "Nom trop long";
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return "Merci d'indiquer prénom et nom";
  if (!NAME_RE.test(n)) return "Le nom contient des caractères invalides";
  return null;
}

function validateCompanyShape(company) {
  const c = (company || '').trim();
  if (c.length < 2) return "Nom de société trop court";
  if (c.length > 200) return "Nom de société trop long";
  return null;
}

const fieldClass = `w-full bg-[#111111] border rounded-xl pl-12 pr-4 py-3.5 text-white placeholder-gray-400
  focus:outline-none focus:ring-2 transition-all duration-200`;
const fieldOk = 'border-gray-800 hover:border-gray-700 focus:border-brand-green/50 focus:ring-brand-green/20';
const fieldErr = 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20';

const DemoEntryForm = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = email, 2 = lead fields, 3 = submitting
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');

  const [precheckLoading, setPrecheckLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [warn, setWarn] = useState('');
  const [knownLead, setKnownLead] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const nameRef = useRef(null);

  useEffect(() => {
    if (step === 2 && !knownLead && nameRef.current) {
      nameRef.current.focus();
    }
  }, [step, knownLead]);

  const handleEmailContinue = async (e) => {
    e?.preventDefault?.();
    setGlobalError('');
    setWarn('');
    setFieldErrors({});
    const emailErr = validateEmailShape(email);
    if (emailErr) {
      setFieldErrors({ email: emailErr });
      return;
    }
    setPrecheckLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/auth/demo-precheck`, {
        email: email.trim().toLowerCase(),
      });
      const data = res?.data || {};
      if (!data.valid) {
        setFieldErrors({ email: data.error || "Email refusé" });
        return;
      }
      if (data.warn) setWarn(data.warn);
      if (data.known) {
        // Lead connu → submit direct, pas de redemande de champs
        setKnownLead(true);
        await submitAccess({ skipValidation: true });
        return;
      }
      // Nouveau lead : demande nom + société
      setKnownLead(false);
      setStep(2);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setGlobalError(typeof detail === 'string' ? detail : "Impossible de vérifier l'email. Réessayez.");
    } finally {
      setPrecheckLoading(false);
    }
  };

  const submitAccess = async ({ skipValidation = false } = {}) => {
    if (!skipValidation) {
      const errs = {};
      const en = validateEmailShape(email);
      if (en) errs.email = en;
      if (!knownLead) {
        const nn = validateNameShape(fullName);
        if (nn) errs.fullName = nn;
        const cn = validateCompanyShape(companyName);
        if (cn) errs.companyName = cn;
      }
      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
        return;
      }
    }
    setFieldErrors({});
    setGlobalError('');
    setSubmitLoading(true);
    try {
      const payload = {
        email: email.trim().toLowerCase(),
      };
      if (!knownLead) {
        payload.full_name = fullName.trim();
        payload.company_name = companyName.trim();
        if (phone.trim()) payload.phone = phone.trim();
        if (jobTitle.trim()) payload.job_title = jobTitle.trim();
      }
      const res = await axios.post(`${API_URL}/api/v1/auth/demo-access`, payload);
      const token = res?.data?.access_token;
      if (!token) {
        setGlobalError("Réponse inattendue du serveur. Réessayez.");
        return;
      }
      localStorage.setItem('access_token', token);
      if (res.data.user) {
        try {
          localStorage.setItem('user', JSON.stringify(res.data.user));
        } catch (_) {
          // ignore
        }
      }
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setGlobalError(typeof detail === 'string' ? detail : "Connexion impossible. Réessayez.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleFinalSubmit = (e) => {
    e.preventDefault();
    submitAccess();
  };

  const isLoading = precheckLoading || submitLoading;

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Accéder à la démo</h2>
        <p className="text-base text-gray-200 leading-relaxed">
          {step === 1
            ? "Entrez votre email professionnel pour découvrir SYLink Pentest."
            : "Bienvenue ! Pour personnaliser votre démo, dites-nous qui vous êtes."}
        </p>
      </div>

      {/* Global error */}
      {globalError && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 backdrop-blur-sm">
          <p className="text-red-400 text-sm flex items-center gap-2">
            <Shield className="w-4 h-4 flex-shrink-0" />
            {globalError}
          </p>
        </div>
      )}

      {/* Email warning (non-blocking) */}
      {warn && (
        <div className="mb-6 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 backdrop-blur-sm">
          <p className="text-yellow-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {warn}
          </p>
        </div>
      )}

      <form onSubmit={step === 1 ? handleEmailContinue : handleFinalSubmit} className="space-y-5" noValidate>
        {/* Email field (always visible) */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2.5">
            Email professionnel <span className="text-brand-green">*</span>
          </label>
          <div className="relative group">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-brand-green transition-colors" />
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (step === 2) {
                  // L'utilisateur revient sur l'email après avoir avancé : on
                  // repasse en step 1 pour re-précheker.
                  setStep(1);
                  setKnownLead(false);
                  setWarn('');
                }
                setFieldErrors((prev) => ({ ...prev, email: undefined }));
              }}
              className={`${fieldClass} ${fieldErrors.email ? fieldErr : fieldOk}`}
              placeholder="vous@votresociete.com"
              autoFocus
              autoComplete="email"
              disabled={isLoading}
            />
          </div>
          {fieldErrors.email && (
            <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" />
              {fieldErrors.email}
            </p>
          )}
        </div>

        {step === 2 && !knownLead && (
          <>
            {/* Full name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2.5">
                Nom complet <span className="text-brand-green">*</span>
              </label>
              <div className="relative group">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-brand-green transition-colors" />
                <input
                  ref={nameRef}
                  type="text"
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, fullName: undefined }));
                  }}
                  className={`${fieldClass} ${fieldErrors.fullName ? fieldErr : fieldOk}`}
                  placeholder="Prénom Nom"
                  autoComplete="name"
                  disabled={isLoading}
                />
              </div>
              {fieldErrors.fullName && (
                <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {fieldErrors.fullName}
                </p>
              )}
            </div>

            {/* Company */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2.5">
                Société <span className="text-brand-green">*</span>
              </label>
              <div className="relative group">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-brand-green transition-colors" />
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, companyName: undefined }));
                  }}
                  className={`${fieldClass} ${fieldErrors.companyName ? fieldErr : fieldOk}`}
                  placeholder="Nom de votre société"
                  autoComplete="organization"
                  disabled={isLoading}
                />
              </div>
              {fieldErrors.companyName && (
                <p className="mt-1.5 text-xs text-red-400 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {fieldErrors.companyName}
                </p>
              )}
            </div>

            {/* Phone (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2.5">
                Téléphone <span className="text-gray-500 text-xs">(facultatif)</span>
              </label>
              <div className="relative group">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-brand-green transition-colors" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={`${fieldClass} ${fieldOk}`}
                  placeholder="+33 6 12 34 56 78"
                  autoComplete="tel"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Job title (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2.5">
                Poste <span className="text-gray-500 text-xs">(facultatif)</span>
              </label>
              <div className="relative group">
                <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-brand-green transition-colors" />
                <input
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  className={`${fieldClass} ${fieldOk}`}
                  placeholder="RSSI, CTO, Pentester…"
                  autoComplete="organization-title"
                  disabled={isLoading}
                />
              </div>
            </div>
          </>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-brand-green hover:bg-brand-green/90 text-black font-semibold py-3.5 rounded-xl
                   transition-all duration-200 flex items-center justify-center gap-2 group whitespace-nowrap
                   disabled:opacity-50 disabled:cursor-not-allowed
                   shadow-lg shadow-brand-green/20 hover:shadow-brand-green/30"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin flex-shrink-0" />
              <span>{submitLoading ? 'Connexion…' : 'Vérification…'}</span>
            </>
          ) : (
            <>
              <span>{step === 1 ? 'Continuer' : 'Accéder à la démo'}</span>
              <ArrowRight className="w-5 h-5 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </button>

        {/* Hint legal/RGPD */}
        <p className="text-xs text-gray-500 text-center leading-relaxed">
          En continuant, vous acceptez d'être contacté par SYLink à propos de
          cette démo. Vos informations sont conservées dans le respect du{' '}
          <a
            href="https://sylink-pentest.com/legal-notice"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-green/80 hover:text-brand-green underline"
          >
            RGPD
          </a>
          .
        </p>
      </form>
    </div>
  );
};

export default DemoEntryForm;
